import type {
    CustomDataValue,
    CustomElement,
    CustomElementFormular
} from "types/shared/helper/customElements";

export type PublishedCustomElement = CustomElement & { fieldKey?: string };
export type PublishedCustomForm = Omit<CustomElementFormular, "elements"> & {
    elements: PublishedCustomElement[];
    disabled?: boolean;
};
type CatalogEntry = {
    element: PublishedCustomElement | null;
    priority: number;
};
export type CustomDataCatalog = ReadonlyMap<string, CatalogEntry>;
export const EMPTY_CUSTOM_DATA_CATALOG: CustomDataCatalog = new Map();

export function buildCustomDataCatalog(
    forms: PublishedCustomForm[]
): CustomDataCatalog {
    const catalog = new Map<string, CatalogEntry>();
    const add = (
        key: string | undefined,
        element: PublishedCustomElement,
        priority: number
    ) => {
        if (typeof key !== "string" || !key.trim()) return;
        const previous = catalog.get(key);
        if (!previous || previous.priority < priority)
            catalog.set(key, { element, priority });
        else if (
            previous.priority === priority &&
            previous.element !== element
        ) {
            catalog.set(key, { element: null, priority });
        }
    };
    for (const form of forms) {
        if (
            form.active !== true ||
            form.disabled ||
            !Array.isArray(form.elements)
        )
            continue;
        for (const element of form.elements) {
            if (element.disabled) continue;
            add(element.fieldKey, element, 3);
            add(element.id, element, 2);
            const internalName = element.internalName;
            if (typeof internalName !== "string") continue;
            const suffix =
                element.internalOnly || element.type === "attachment"
                    ? "_internal"
                    : "";
            add(internalName + suffix, element, 1);
            add(internalName.replace(/&|\(|\)/g, "") + suffix, element, 1);
        }
    }
    return catalog;
}

function title(texts: CustomElement["texts"] | undefined, language: string) {
    for (const lang of [
        ...new Set([language.toLowerCase().split("-")[0], "de", "en"])
    ]) {
        const value = texts?.[lang]?.title;
        if (typeof value === "string" && value.trim()) return value.trim();
    }
    return "";
}

function optionTitle(
    element: PublishedCustomElement,
    code: string,
    language: string
) {
    const options = element.options ?? [];
    const byValue = options.filter((option) => String(option.value) === code);
    const matches = byValue.length
        ? byValue
        : options.filter((option) => option.internalName === code);
    if (matches.length !== 1) return "";
    return title(matches[0].texts, language) || matches[0].internalName || code;
}

export function presentCustomData(
    catalog: CustomDataCatalog,
    key: string,
    value: CustomDataValue,
    language = "de"
): { label: string; value: CustomDataValue } {
    const element = catalog.get(key)?.element;
    const fallback = key.replace(/_internal$/, "");
    const label =
        key === "balance"
            ? "Bisher bestellt"
            : element
            ? title(element.texts, language) || element.internalName || fallback
            : fallback;
    if (!element?.options?.length) return { label, value };
    if (typeof value === "string" || typeof value === "number") {
        return {
            label,
            value: optionTitle(element, String(value), language) || value
        };
    }
    if (value && typeof value === "object") {
        return {
            label,
            value: Object.entries(value)
                .map(([code, answer]) => {
                    const translated = optionTitle(element, code, language);
                    if (translated && (answer === false || answer === null))
                        return "";
                    const name = translated || code;
                    return answer === true
                        ? name
                        : `${name}: ${String(answer ?? "")}`;
                })
                .filter(Boolean)
                .join(", ")
        };
    }
    return { label, value };
}
